import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import PublicNav from '../components/layout/PublicNav';
import PublicFooter from '../components/layout/PublicFooter';

// ── Scroll-reveal + auto-cycling animated step flow ─────────────────────────

function useInView(threshold = 0.25) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setInView(true); obs.disconnect(); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function AnimatedStepFlow({ steps, color }) {
  const [ref, inView] = useInView(0.2);
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const id = setInterval(() => setActive(a => (a + 1) % steps.length), 1700);
    return () => clearInterval(id);
  }, [inView, steps.length]);

  return (
    <div ref={ref} className="step-flow">
      {steps.map(({ n, icon, title, desc }, i) => {
        const done = active > i;
        const isActive = active === i;
        return (
          <Fragment key={n}>
            <div
              className="step-flow-item"
              style={{
                flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
                padding: '0 0.75rem', maxWidth: 220,
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(24px)',
                transition: `opacity .6s ease ${i * 0.15}s, transform .6s cubic-bezier(.34,1.56,.64,1) ${i * 0.15}s`,
              }}
            >
              <div
                key={isActive ? `${n}-active` : `${n}-idle`}
                className={isActive ? 'step-flow-node step-flow-node-bounce' : 'step-flow-node'}
                style={{
                  width: 56, height: 56, borderRadius: '50%', flexShrink: 0, marginBottom: '0.875rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.2rem', fontWeight: 800,
                  background: done ? '#00C878' : isActive ? color : 'var(--surface-2)',
                  border: `2px solid ${done ? '#00C878' : isActive ? color : 'var(--border-bright)'}`,
                  boxShadow: isActive ? `0 0 0 8px ${color}22` : 'none',
                  color: (done || isActive) ? '#fff' : 'var(--text-3)',
                  transform: isActive ? undefined : 'scale(1)',
                  transition: 'background .28s ease, border-color .28s ease, box-shadow .28s ease, color .28s ease, transform .28s ease',
                }}
              >
                {icon ? icon : (done ? '✓' : n)}
              </div>
              <div className="step-flow-text">
                <h4 style={{ fontSize: '0.92rem', fontWeight: 700, color: 'var(--text-1)', marginBottom: '0.35rem' }}>{n}. {title}</h4>
                <p style={{ fontSize: '0.78rem', color: 'var(--text-3)', lineHeight: 1.55 }}>{desc}</p>
              </div>
            </div>
            {i < steps.length - 1 && (
              <div className="step-flow-connector">
                <span style={{
                  position: 'absolute', inset: 0, background: '#00C878', borderRadius: 2,
                  transform: active > i ? 'scaleX(1)' : 'scaleX(0)', transformOrigin: 'left',
                  transition: 'transform .38s cubic-bezier(.34,1.56,.64,1)',
                }} />
              </div>
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

// ── Services carousel: continuously-sliding cards + click-to-focus expansion ─

function ServicesCarousel({ services }) {
  const [sectionRef, sectionInView] = useInView(0.1);
  const [focused, setFocused] = useState(null);
  const [nudging, setNudging] = useState(false);
  const trackRef = useRef(null);
  const offsetRef = useRef(0);
  const loopWidthRef = useRef(0);
  const rafRef = useRef(null);
  const lastTimeRef = useRef(null);
  const nudgingRef = useRef(false);
  const nudgeTimeoutRef = useRef(null);
  const loopServices = [...services, ...services];

  useEffect(() => { nudgingRef.current = nudging; }, [nudging]);

  useEffect(() => {
    const measure = () => { if (trackRef.current) loopWidthRef.current = trackRef.current.scrollWidth / 2; };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [services.length]);

  useEffect(() => {
    const speed = 45; // px per second
    const tick = now => {
      if (lastTimeRef.current == null) lastTimeRef.current = now;
      const dt = (now - lastTimeRef.current) / 1000;
      lastTimeRef.current = now;
      if (!focused && sectionInView && !nudgingRef.current) {
        const loopWidth = loopWidthRef.current || 1;
        offsetRef.current = (offsetRef.current + speed * dt) % loopWidth;
        if (trackRef.current) trackRef.current.style.transform = `translateX(-${offsetRef.current}px)`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(rafRef.current); lastTimeRef.current = null; };
  }, [focused, sectionInView]);

  useEffect(() => {
    if (!focused) return;
    const onKey = e => { if (e.key === 'Escape') setFocused(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focused]);

  const nudge = dir => {
    const loopWidth = loopWidthRef.current || 1;
    const cardStep = loopWidth / services.length;
    offsetRef.current = ((offsetRef.current + dir * cardStep) % loopWidth + loopWidth) % loopWidth;
    if (trackRef.current) trackRef.current.style.transform = `translateX(-${offsetRef.current}px)`;
    setNudging(true);
    clearTimeout(nudgeTimeoutRef.current);
    nudgeTimeoutRef.current = setTimeout(() => setNudging(false), 450);
  };

  return (
    <div ref={sectionRef} className={`services-carousel ${focused ? 'is-focused' : ''}`}>
      {!focused && (
        <>
          <button className="services-carousel-side-arrow services-carousel-side-arrow-left" onClick={() => nudge(-1)} aria-label="Scroll back">‹</button>
          <button className="services-carousel-side-arrow services-carousel-side-arrow-right" onClick={() => nudge(1)} aria-label="Scroll forward">›</button>
        </>
      )}
      <div className="services-carousel-viewport" style={{ opacity: sectionInView ? 1 : 0, transition: 'opacity .6s ease' }}>
        <div ref={trackRef} className={`services-carousel-track ${nudging ? 'is-nudging' : ''}`}>
          {loopServices.map(({ icon, name, desc, color, bg, border, live, to }, i) => (
            <div key={`${name}-${i}`} className="services-carousel-slide">
              <div
                className="services-carousel-card"
                style={{ background: 'var(--bg)', borderColor: border }}
                onClick={() => setFocused({ icon, name, desc, color, bg, border, live, to })}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <div className="services-carousel-card-icon" style={{ background: bg, borderColor: border }}>{icon}</div>
                  {live ? (
                    <span style={{ fontSize: '1rem', fontWeight: 700, color, background: bg, border: `1px solid ${border}`, padding: '0.31rem 0.94rem', borderRadius: 99 }}>Live</span>
                  ) : (
                    <span style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-3)', background: 'var(--surface)', border: '1px solid var(--border)', padding: '0.31rem 0.94rem', borderRadius: 99 }}>Soon</span>
                  )}
                </div>
                <h3 style={{ fontSize: '1.56rem', fontWeight: 700, color: 'var(--text-1)', marginBottom: '0.625rem', fontFamily: 'Space Grotesk, sans-serif' }}>{name}</h3>
                <p style={{ fontSize: '1.28rem', color: 'var(--text-2)', lineHeight: 1.6 }}>{desc}</p>
                <button className="services-carousel-expand" style={{ color, borderColor: border }} aria-label={`Expand ${name}`} onClick={e => { e.stopPropagation(); setFocused({ icon, name, desc, color, bg, border, live, to }); }}>+</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {focused && (
        <div className="services-focus-overlay" onClick={() => setFocused(null)}>
          <div className="services-focus-card" style={{ background: `linear-gradient(160deg, ${focused.bg}, var(--surface))`, borderColor: focused.border }} onClick={e => e.stopPropagation()}>
            <button className="services-focus-close" onClick={() => setFocused(null)} aria-label="Close">−</button>
            <div className="services-focus-icon" style={{ background: focused.bg, borderColor: focused.border }}>{focused.icon}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.75rem' }}>
              <h3 style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-1)', fontFamily: 'Space Grotesk, sans-serif' }}>{focused.name}</h3>
              {focused.live ? (
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: focused.color, background: focused.bg, border: `1px solid ${focused.border}`, padding: '0.2rem 0.7rem', borderRadius: 99 }}>Live</span>
              ) : (
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-3)', background: 'var(--surface)', border: '1px solid var(--border)', padding: '0.2rem 0.7rem', borderRadius: 99 }}>Soon</span>
              )}
            </div>
            <p style={{ fontSize: '0.95rem', color: 'var(--text-2)', lineHeight: 1.75, marginBottom: '1.5rem' }}>{focused.desc}</p>
            {focused.live && focused.to ? (
              <Link to={focused.to} className="btn btn-primary" style={{ alignSelf: 'flex-start' }}>Get started →</Link>
            ) : (
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-3)' }}>Coming soon</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Services data ─────────────────────────────────────────────────────────────

const SERVICES = [
  {
    icon: '⚡',
    name: 'Quick Loans',
    desc: 'Access personal, business & emergency loans up to ₦5M within 24 hours.',
    color: '#00C896',
    bg: 'rgba(0,200,150,0.1)',
    border: 'rgba(0,200,150,0.2)',
    live: true,
    to: '/loans/apply',
  },
  {
    icon: '📱',
    name: 'Airtime',
    desc: 'Top up airtime for MTN, Airtel, Glo & 9mobile instantly at best rates.',
    color: '#818CF8',
    bg: 'rgba(129,140,248,0.1)',
    border: 'rgba(129,140,248,0.2)',
    live: false,
  },
  {
    icon: '🌐',
    name: 'Data Bundles',
    desc: 'Purchase data plans for all Nigerian networks. Fast and hassle-free.',
    color: '#5B8EFF',
    bg: 'rgba(91,142,255,0.1)',
    border: 'rgba(91,142,255,0.2)',
    live: false,
  },
  {
    icon: '💡',
    name: 'Electricity',
    desc: 'Pay PHCN / EKEDC / IBEDC bills and get your prepaid token instantly.',
    color: '#F5A623',
    bg: 'rgba(245,166,35,0.1)',
    border: 'rgba(245,166,35,0.2)',
    live: false,
  },
  {
    icon: '📺',
    name: 'Cable TV',
    desc: 'Renew DStv, GOtv, Startimes & Showmax subscriptions in seconds.',
    color: '#FF6B6B',
    bg: 'rgba(255,107,107,0.1)',
    border: 'rgba(255,107,107,0.2)',
    live: false,
  },
  {
    icon: '📊',
    name: 'Credit Report',
    desc: 'Check your credit score, history and improve your borrowing power.',
    color: '#00C8D4',
    bg: 'rgba(0,200,212,0.1)',
    border: 'rgba(0,200,212,0.2)',
    live: true,
    to: '/credit-report',
  },
  {
    icon: '💰',
    name: 'Savings Plans',
    desc: 'Set a goal, contribute on your schedule, and build a Trust Score that unlocks early access to your funds.',
    color: '#1596E0',
    bg: 'rgba(21,150,224,0.1)',
    border: 'rgba(21,150,224,0.2)',
    live: true,
    to: '/contributions/new',
  },
  {
    icon: '👥',
    name: 'Group Ajo',
    desc: 'Join or start a rotating group contribution circle — everyone pays in, the pot rotates until each member is paid.',
    color: '#F5A623',
    bg: 'rgba(245,166,35,0.1)',
    border: 'rgba(245,166,35,0.2)',
    live: true,
    to: '/groups/new',
  },
];

const SAVINGS_STEPS = [
  { n: '1', title: 'Set a Goal', desc: 'Choose an amount, contribution cycle and duration.' },
  { n: '2', title: 'Contribute Steadily', desc: 'Make contributions on your own schedule — weekly, biweekly or monthly.' },
  { n: '3', title: 'Build Trust Score', desc: 'Consistent, on-time contributions raise your score.' },
  { n: '4', title: 'Access Anytime', desc: 'Withdraw up to 40% free with a Progress Card, or take a fee-based Plan Advance.' },
];

const GROUP_STEPS = [
  { n: '1', icon: '👥', title: 'Form a Group', desc: 'Set the contribution amount, cycle and number of members.' },
  { n: '2', icon: '✉️', title: 'Invite Members', desc: 'Share your invite code or send email invites to fill every slot.' },
  { n: '3', icon: '🤝', title: 'Contribute Together', desc: 'Every member pays the same amount into the shared pot each cycle.' },
  { n: '4', icon: '🔄', title: 'Get Paid in Turns', desc: 'The full pot rotates to one member per cycle until everyone has been paid.' },
];

const STEPS = [
  { n: '01', title: 'Create Account', desc: 'Sign up in under 2 minutes with just your email.' },
  { n: '02', title: 'Verify Identity', desc: 'Complete a simple KYC process to unlock all services.' },
  { n: '03', title: 'Choose a Service', desc: 'Apply for a loan, buy airtime, data, pay bills — all in one place.' },
  { n: '04', title: 'Get Results Fast', desc: 'Loans disbursed within 24 hours. Bill payments are instant.' },
];

const STATS = [
  { value: '10,000+', label: 'Active Users' },
  { value: '₦2B+',   label: 'Disbursed' },
  { value: '4.9★',   label: 'App Rating' },
  { value: '< 24h',  label: 'Loan Disbursement' },
];

// ── Landing ───────────────────────────────────────────────────────────────────

export default function Landing() {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  return (
    <div style={{ background: 'transparent', color: 'var(--text-1)', minHeight: '100vh', fontFamily: 'Inter, sans-serif', display: 'flex', flexDirection: 'column' }}>

      <PublicNav />

      <main style={{ flex: 1 }}>

      {/* ── HERO ── */}
      <section className="hero-section" style={{
        padding: '1.25rem 5% 5rem',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
      }}>
        {/* Glow blobs */}
        <div style={{ position: 'absolute', top: -120, left: '20%', width: 480, height: 480, borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,200,150,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: -60, right: '15%', width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle, rgba(129,140,248,0.1) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <div style={{
          position: 'relative', width: '100%', maxWidth: 1200, margin: '0 auto',
          display: 'grid', gridTemplateColumns: '1.05fr 0.95fr', gap: '3rem', alignItems: 'center',
        }}>
          {/* Left — info */}
          <div style={{ textAlign: 'left' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.35rem 1rem', borderRadius: 99,
              background: 'rgba(0,200,150,0.1)', border: '1px solid rgba(0,200,150,0.25)',
              fontSize: '0.78rem', fontWeight: 600, color: '#00C896',
              marginBottom: '1.75rem',
            }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#00C896', display: 'inline-block', boxShadow: '0 0 8px #00C896' }} />
              Nigeria's All-in-One Financial Platform
            </div>

            <h1 style={{
              fontSize: 'clamp(2.2rem, 4.2vw, 3.6rem)',
              fontWeight: 900,
              fontFamily: 'Space Grotesk, sans-serif',
              lineHeight: 1.1,
              letterSpacing: '-0.03em',
              marginBottom: '1.25rem',
            }}>
              One Platform for All<br />
              <span style={{ background: 'linear-gradient(135deg,#00C896 20%,#818CF8 80%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Your Financial Needs
              </span>
            </h1>

            <p style={{ fontSize: '1.05rem', color: 'var(--text-2)', lineHeight: 1.7, marginBottom: '2.25rem', maxWidth: 500 }}>
              Loans, airtime, data, electricity bills, cable TV subscriptions, and credit reports — fast, secure, and all in one place.
            </p>

            <div style={{ display: 'flex', gap: '0.875rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
              <Link to="/register" className="btn btn-primary" style={{ fontSize: '1rem', padding: '0.75rem 2rem', borderRadius: 12, fontWeight: 700 }}>
                Open Free Account
              </Link>
              <Link to="/login" className="btn btn-ghost" style={{ fontSize: '1rem', padding: '0.75rem 1.75rem', borderRadius: 12 }}>
                Sign In →
              </Link>
            </div>

            {/* Stats */}
            <div className="hero-stats-row">
              {STATS.map(({ value, label }) => (
                <div key={label} className="hero-stat">
                  <div className="hero-stat-value">{value}</div>
                  <div className="hero-stat-label">{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — animated mobile app mockup (sample logged-in homepage) */}
          <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
            <div className="hero-phone-wrap">
              <div className="hero-phone">
                <div className="hero-phone-notch" />
                <div className="hero-phone-screen">
                  <div className="hero-phone-greeting">
                    <div className="hero-phone-avatar" />
                    <div>
                      <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Welcome back</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)' }}>Adaeze O.</div>
                    </div>
                  </div>

                  <div className="hero-phone-stats-row">
                    <div className="hero-phone-stat">
                      <div className="hero-phone-stat-value">₦450,000</div>
                      <div className="hero-phone-stat-label">Total Repaid</div>
                    </div>
                    <div className="hero-phone-stat">
                      <div className="hero-phone-stat-value">742</div>
                      <div className="hero-phone-stat-label">Credit Score</div>
                    </div>
                  </div>

                  <div className="hero-phone-loan-card">
                    <div className="hero-phone-loan-tag">Active Loan</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                      <div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-1)', fontFamily: 'Space Grotesk, sans-serif' }}>₦250,000</div>
                        <div style={{ fontSize: 10, color: 'var(--text-3)' }}>LN-2026-0091</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>₦45,200<span style={{ fontSize: 10, color: 'var(--text-3)', fontWeight: 500 }}>/mo</span></div>
                        <div style={{ fontSize: 10, color: 'var(--primary)', fontWeight: 600 }}>View repayments →</div>
                      </div>
                    </div>
                  </div>

                  <div className="hero-phone-section-label">Quick Services</div>
                  <div className="hero-phone-actions">
                    {[
                      { icon: '⚡', label: 'Loans', color: '#00C896' },
                      { icon: '📱', label: 'Airtime', color: '#818CF8' },
                      { icon: '🌐', label: 'Data', color: '#5B8EFF' },
                      { icon: '💡', label: 'Electricity', color: '#F5A623' },
                      { icon: '📺', label: 'Cable TV', color: '#FF6B6B' },
                      { icon: '📊', label: 'Credit', color: '#00C8D4' },
                    ].map(s => (
                      <div key={s.label} className="hero-phone-action">
                        <div className="hero-phone-action-icon" style={{ background: `${s.color}22`, border: `1px solid ${s.color}45` }}>{s.icon}</div>
                        <span>{s.label}</span>
                      </div>
                    ))}
                  </div>

                  <div className="hero-phone-section-label">Recent Activity</div>
                  <div className="hero-phone-activity">
                    <div className="hero-phone-activity-row">
                      <span>Loan Disbursed</span>
                      <span className="hero-phone-activity-amt hero-phone-activity-pos">+₦250,000</span>
                    </div>
                    <div className="hero-phone-activity-row">
                      <span>Repayment</span>
                      <span className="hero-phone-activity-amt">-₦45,200</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section style={{ padding: '5rem 5%', background: 'var(--surface)' }}>
        <div style={{ maxWidth: 1650, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#00C896', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '0.75rem' }}>Our Services</div>
            <h2 style={{ fontSize: 'clamp(1.6rem,3vw,2.4rem)', fontWeight: 800, fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '-0.02em', marginBottom: '0.75rem' }}>
              Everything You Need, One Platform
            </h2>
            <p style={{ fontSize: '0.95rem', color: 'var(--text-2)', maxWidth: 480, margin: '0 auto' }}>
              From instant loans to utility payments, manage all your finances without switching apps.
            </p>
          </div>

          <ServicesCarousel services={SERVICES} />
        </div>
      </section>

      {/* ── SAVINGS PLANS FLOW ── */}
      <section style={{ padding: '5rem 5%', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '10%', left: '-8%', width: 380, height: 380, borderRadius: '50%', background: 'radial-gradient(circle, rgba(21,150,224,0.1) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ maxWidth: 1000, margin: '0 auto', position: 'relative' }}>
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 1rem', borderRadius: 99, background: 'rgba(21,150,224,0.1)', border: '1px solid rgba(21,150,224,0.25)', marginBottom: '1rem' }}>
              <span>💰</span>
              <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#1596E0', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Individual · Savings Plans</span>
            </div>
            <h2 style={{ fontSize: 'clamp(1.6rem,3vw,2.2rem)', fontWeight: 800, fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '-0.02em', marginBottom: '0.75rem' }}>
              How Savings Plans Work
            </h2>
            <p style={{ fontSize: '0.95rem', color: 'var(--text-2)', maxWidth: 520, margin: '0 auto' }}>
              Your plan, your money — no shared risk. Save toward a goal at your own pace and unlock early access as your Trust Score grows.
            </p>
          </div>

          <AnimatedStepFlow steps={SAVINGS_STEPS} color="#1596E0" />

          <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
            <Link to="/contributions/new" className="btn btn-primary btn-lg">Start Saving →</Link>
          </div>
        </div>
      </section>

      {/* ── GROUP AJO FLOW ── */}
      <section style={{ padding: '5rem 5%', background: 'var(--surface)', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', bottom: '5%', right: '-8%', width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle, rgba(245,166,35,0.1) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ maxWidth: 1000, margin: '0 auto', position: 'relative' }}>
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 1rem', borderRadius: 99, background: 'rgba(245,166,35,0.1)', border: '1px solid rgba(245,166,35,0.25)', marginBottom: '1rem' }}>
              <span>👥</span>
              <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#F5A623', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Group · Ajo / Esusu</span>
            </div>
            <h2 style={{ fontSize: 'clamp(1.6rem,3vw,2.2rem)', fontWeight: 800, fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '-0.02em', marginBottom: '0.75rem' }}>
              How Group Contributions Work
            </h2>
            <p style={{ fontSize: '0.95rem', color: 'var(--text-2)', maxWidth: 520, margin: '0 auto' }}>
              The traditional rotating Ajo/Esusu, digitized. Contribute with people you trust and take turns receiving the full pot.
            </p>
          </div>

          <AnimatedStepFlow steps={GROUP_STEPS} color="#F5A623" />

          <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
            <Link to="/groups/new" className="btn btn-gold btn-lg">Start a Group →</Link>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section style={{ padding: '5rem 5%' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#818CF8', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '0.75rem' }}>How It Works</div>
            <h2 style={{ fontSize: 'clamp(1.6rem,3vw,2.2rem)', fontWeight: 800, fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '-0.02em' }}>
              Up and Running in Minutes
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1.5rem' }}>
            {STEPS.map(({ n, title, desc }) => (
              <div key={n} style={{ position: 'relative', padding: '1.5rem' }}>
                <div style={{ fontSize: '2.5rem', fontWeight: 900, color: 'var(--step-number)', fontFamily: 'Space Grotesk, sans-serif', marginBottom: '0.75rem', lineHeight: 1 }}>{n}</div>
                <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-1)', marginBottom: '0.4rem' }}>{title}</h4>
                <p style={{ fontSize: '0.82rem', color: 'var(--text-2)', lineHeight: 1.6 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY US ── */}
      <section style={{ padding: '4rem 5%', background: 'var(--surface)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#F5A623', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '0.75rem' }}>Why PCredit</div>
            <h2 style={{ fontSize: 'clamp(1.5rem,2.5vw,2.1rem)', fontWeight: 800, fontFamily: 'Space Grotesk, sans-serif', marginBottom: '1.25rem', lineHeight: 1.2 }}>
              Built for Speed,<br />Designed for Trust
            </h2>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-2)', lineHeight: 1.75, marginBottom: '1.5rem' }}>
              We've simplified financial services for Nigerians — no paperwork, no long queues, no hidden fees. Everything is transparent and processed instantly.
            </p>
            <Link to="/register" className="btn btn-primary" style={{ fontSize: '0.9rem', padding: '0.65rem 1.5rem' }}>Create Free Account →</Link>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            {[
              { icon: '⚡', title: 'Instant Processing', desc: 'Loan decisions in minutes, disbursement within 24 hours.' },
              { icon: '🔒', title: 'Bank-Grade Security', desc: 'Your data and money are protected with 256-bit encryption.' },
              { icon: '📞', title: '24/7 Support', desc: 'Get help anytime via chat, email or phone.' },
              { icon: '📉', title: 'Competitive Rates', desc: 'Transparent pricing with no hidden charges ever.' },
            ].map(({ icon, title, desc }) => (
              <div key={title} style={{ padding: '1.25rem', background: 'var(--bg)', borderRadius: 14, border: '1px solid var(--border)' }}>
                <div style={{ fontSize: '1.4rem', marginBottom: '0.6rem' }}>{icon}</div>
                <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--text-1)', marginBottom: '0.3rem' }}>{title}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-2)', lineHeight: 1.55 }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      </main>

      <PublicFooter />
    </div>
  );
}
